import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "../../components/Dropdown";
import HotDropdown from "../../components/HotDropdown";
import {
  getFilteredSuppliers,
  getSupplierByBank,
} from "../../services/suppliers/suppliersServices";
import { getFilteredSubcompanies } from "../../services/subcompanies/subcompaniesServices";
import { UserAuth } from "../../utils/AuthContext";
import useLanguage from "../../utils/useLanguage";

const CompanySupplierFilter = ({
  onCompanyChange,
  onSupplierChange,
  disabled = false,
  showCompany = true,
  showSupplier = true,
  companyTitle = "company",
  supplierTitle = "supplier",
  supplier = {
    id: "",
    name: "All",
  },
  subcompany = {
    id: "All",
    name: "All",
  },
  myTransactions,
  setMyTransactions,
}) => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  // States for companies - only need selectedCompany state now
  const [selectedCompany, setSelectedCompany] = useState(subcompany);

  const [selectedSupplier, setSelectedSupplier] = useState(supplier);
  const { userData } = useContext(UserAuth);

  // We keep a single loading state for initial operations or when fetching data
  const [loading, setLoading] = useState(false);

  // Fetch companies - now returns response for HotDropdown
  const fetchCompanies = async (
    page = 1,
    limit = 5,
    isDropDown = false,
    searchTerm = ""
  ) => {
    try {
      const response = await getFilteredSubcompanies({
        page: page,
        limit: limit,
        search: searchTerm,
        organizationName: userData.organizationName,
      });

      if (response.status === 200) {
        const mappedData = response.data.subCompanies.map((company) => ({
          ...company,
          name: currentDirection === "rtl" ? company.nameInAr : company.name,
        }));

        // Return the response object with the mapped data and total count
        return {
          ...response,
          data: {
            ...response.data,
            companies: mappedData,
            totalCount: response.data.totalCount,
          },
        };
      }
      return {
        status: response.status,
        data: { companies: [], totalCount: 0 },
      };
    } catch (error) {
      console.error("Error fetching companies:", error);
      return { status: 500, data: { companies: [], totalCount: 0 } };
    }
  };

  // Fetch suppliers
  const fetchSuppliers = async (
    page = 1,
    limit = 5,
    isDropDown = false,
    searchTerm = ""
  ) => {
    try {
      const response = await getSupplierByBank({
        page,
        limit,
        search: searchTerm,
      });

      return response;
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      return {
        status: 500,
        data: {
          suppliers: [],
          totalPages: 1,
          totalRecords: 0,
        },
      };
    }
  };

  // Handle company selection
  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    if (onCompanyChange) {
      onCompanyChange(company);
    }
  };

  // Handle supplier selection
  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    if (onSupplierChange) {
      onSupplierChange(supplier);
    }
  };

  const [selectedFilter, setSelectedFilter] = useState({
    title: "all",
    value: "all",
  });

  // Handle transactions selection
  const handleTransactionsSelect = (option) => {
    setSelectedFilter(option);
    setMyTransactions(option.value);
  };

  const filterOptions = [
    { id: 1, title: "myApplications", value: "myApplications" },
    { id: 2, title: "all", value: "all" },
  ];

  return (
    <div className="u-margin--top_400">
      <div className="u-margin--top_400 u-margin--bottom_400 o-row o-row--vertical-align_center u-margin--bottom_300 o-row--gap-size_100 u-width--max-width_650">
        <label className="u-min-width-specific_100">
          {t("theApplications")}:{" "}
        </label>
        <Dropdown
          flex
          options={filterOptions}
          setSelected={handleTransactionsSelect}
          selected={selectedFilter}
        />
      </div>

      {showCompany && (
        <section className="o-row o-row--vertical-align_center u-margin--bottom_300 o-row--gap-size_100 u-width--max-width_650">
          <label className="u-min-width-specific_100">
            {t(companyTitle)}:{" "}
          </label>
          <HotDropdown
            initOptions={[{ id: "All", name: "All" }]}
            selected={selectedCompany}
            setSelected={handleCompanySelect}
            optionsName={"companies"}
            disabled={disabled || loading}
            prefetch={true}
            getData={fetchCompanies}
            allowSameSelect={false}
            flex
          />
        </section>
      )}

      {showSupplier && (
        <section className="o-row o-row--vertical-align_center u-margin--bottom_300 o-row--gap-size_100 u-width--max-width_650">
          <label className="u-min-width-specific_100">
            {t(supplierTitle)}:{" "}
          </label>
          <HotDropdown
            initOptions={[{ id: "", name: "All" }]}
            selected={selectedSupplier}
            setSelected={handleSupplierSelect}
            optionsName={"suppliers"}
            disabled={disabled || loading}
            prefetch={true}
            getData={fetchSuppliers}
            allowSameSelect={false}
            flex
          />
        </section>
      )}
    </div>
  );
};

export default CompanySupplierFilter;
